import proj4 from 'proj4';
import { Extent } from './Extent';

export class LineString {
  public coordinates: [number, number][];
  public type: 'LineString' = 'LineString';
  private srid: string;

  constructor(coordinates: [number, number][], srid: string = 'EPSG:4326') {
    this.coordinates = coordinates.map((c) => [...c] as [number, number]);
    this.srid = srid;
  }

  getCoordinates(): [number, number][] {
    return this.coordinates.map((c) => [...c] as [number, number]);
  }

  setCoordinates(coords: [number, number][]): void {
    this.coordinates = coords.map((c) => [...c] as [number, number]);
  }

  getLength(): number {
    let length = 0;
    for (let i = 1; i < this.coordinates.length; i++) {
      const dx = this.coordinates[i][0] - this.coordinates[i - 1][0];
      const dy = this.coordinates[i][1] - this.coordinates[i - 1][1];
      length += Math.sqrt(dx * dx + dy * dy);
    }
    return length;
  }

  getGeodesicLength(): number {
    let length = 0;
    for (let i = 1; i < this.coordinates.length; i++) {
      length += haversineDistance(this.coordinates[i - 1], this.coordinates[i]);
    }
    return length;
  }

  getCoordinateAt(fraction: number): [number, number] {
    if (fraction <= 0) return [...this.coordinates[0]] as [number, number];
    if (fraction >= 1) return [...this.coordinates[this.coordinates.length - 1]] as [number, number];

    const totalLength = this.getLength();
    const targetLength = fraction * totalLength;
    let accumulated = 0;

    for (let i = 1; i < this.coordinates.length; i++) {
      const dx = this.coordinates[i][0] - this.coordinates[i - 1][0];
      const dy = this.coordinates[i][1] - this.coordinates[i - 1][1];
      const segLength = Math.sqrt(dx * dx + dy * dy);

      if (accumulated + segLength >= targetLength) {
        const f = (targetLength - accumulated) / segLength;
        return [
          this.coordinates[i - 1][0] + dx * f,
          this.coordinates[i - 1][1] + dy * f,
        ];
      }
      accumulated += segLength;
    }

    return [...this.coordinates[this.coordinates.length - 1]] as [number, number];
  }

  getFirstCoordinate(): [number, number] {
    return [...this.coordinates[0]] as [number, number];
  }

  getLastCoordinate(): [number, number] {
    return [...this.coordinates[this.coordinates.length - 1]] as [number, number];
  }

  appendCoordinate(coord: [number, number]): void {
    this.coordinates.push([...coord]);
  }

  getExtent(): Extent {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of this.coordinates) {
      if (c[0] < minX) minX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] > maxY) maxY = c[1];
    }
    return new Extent(minX, minY, maxX, maxY);
  }

  simplify(tolerance: number): LineString {
    const simplified = douglasPeucker(this.coordinates, tolerance);
    return new LineString(simplified, this.srid);
  }

  clone(): LineString {
    return new LineString(this.getCoordinates(), this.srid);
  }

  transform(fromProj: string, toProj: string): LineString {
    const transformed = this.coordinates.map(
      (c) => proj4(fromProj, toProj, [...c]) as [number, number]
    );
    return new LineString(transformed, toProj);
  }

  toGeoJSON(): GeoJSON.LineString {
    return {
      type: 'LineString',
      coordinates: this.getCoordinates(),
    };
  }

  forEachSegment(callback: (start: [number, number], end: [number, number]) => boolean | void): void {
    for (let i = 0; i < this.coordinates.length - 1; i++) {
      const result = callback(this.coordinates[i], this.coordinates[i + 1]);
      if (result === true) break;
    }
  }
}

function haversineDistance(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function douglasPeucker(coords: [number, number][], tolerance: number): [number, number][] {
  if (coords.length <= 2) return coords;

  let maxDist = 0;
  let maxIdx = 0;
  const start = coords[0];
  const end = coords[coords.length - 1];

  for (let i = 1; i < coords.length - 1; i++) {
    const dist = pointToLineDistance(coords[i], start, end);
    if (dist > maxDist) {
      maxDist = dist;
      maxIdx = i;
    }
  }

  if (maxDist > tolerance) {
    const left = douglasPeucker(coords.slice(0, maxIdx + 1), tolerance);
    const right = douglasPeucker(coords.slice(maxIdx), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  return [start, end];
}

function pointToLineDistance(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  const dx = lineEnd[0] - lineStart[0];
  const dy = lineEnd[1] - lineStart[1];
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return Math.sqrt((point[0] - lineStart[0]) ** 2 + (point[1] - lineStart[1]) ** 2);
  return Math.abs(dy * point[0] - dx * point[1] + lineEnd[0] * lineStart[1] - lineEnd[1] * lineStart[0]) / len;
}
